import React from "react";
import { getLocale } from "react-i18nify";

import {
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Area,
  AreaChart,
} from "recharts";
import { Box, Typography } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";

import font from "src/styles/font";
import device from "src/styles/device";
import { DarkGrey, Primary, White } from "src/styles/color";
import { convertIntToCurrency } from "src/common";

import LabelSection from "src/components/core/LabelSection";
import dayjs from "dayjs";

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <Box
        sx={{
          backgroundColor: White,
          color: Primary,
          padding: "4px 10px",
          borderRadius: "12px",
          fontSize: 14,
          border: `1px solid ${Primary}`,
          boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
          zIndex: 100,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontSize: 12,
            fontWeight: 400,
            color: Primary,
          }}
        >
          {dayjs(payload[0].payload.date).format("DD MMM")}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            fontSize: 14,
            fontWeight: 500,
            color: Primary,
          }}
        >
          {convertIntToCurrency(payload[0].value, false)} views
        </Typography>
      </Box>
    );
  }
  return null;
};

const CustomXAxisTick = ({ x, y, payload }) => {
  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dy={16}
        fontSize={font.size.sm}
        fontWeight={500}
        fill="#666"
        textAnchor="middle"
        // transform="rotate(-45)"
      >
        {dayjs(payload.value).format("DD MMM")}
      </text>
    </g>
  );
};

const getDivision = (value) => {
  if (value < 500) {
    return 100;
  } else if (value < 10000) {
    return 1000;
  } else if (value < 100000) {
    return 10000;
  } else if (value < 1000000) {
    return 100000;
  } else {
    return 1000000;
  }
};

const findNearestMaxValue = (data) => {
  const maxValue = Math.max(...data.map((item) => item.screenPageViews));
  const division = getDivision(maxValue);
  const nearestMaxValue = Math.ceil(maxValue / division) * division;
  return nearestMaxValue;
};

const DailyViewsChart = ({
  data = [],
  selectedTimeRangeData,
  isSeller = false,
}) => {
  const nearestMaxValue = findNearestMaxValue(data);
  const yAxisDomain = [0, nearestMaxValue];
  const isSmallerThanMobileL = useMediaQuery(device.mobileL);
  const isSmallerThanTabletL = useMediaQuery(device.tabletL);
  const isSmallerThanDesktop = useMediaQuery(device.desktop);

  const getXInterval = (data) => {
    if (isSmallerThanMobileL) {
      return data.length > 4 ? Math.floor(data.length / 4) : 1;
    } else if (isSmallerThanTabletL) {
      return data.length > 6 ? Math.floor(data.length / 6) : 1;
    } else if (isSmallerThanDesktop) {
      return data.length > 8 ? Math.floor(data.length / 8) : 1;
    }
    return data.length > 10 ? Math.floor(data.length / 10) : 1;
  };

  const xInterval = getXInterval(data);

  return (
    <LabelSection
      title={getLocale() === "th" ? "ยอดวิวรวมรายวัน" : "Daily Views"}
      description={
        <Typography
          variant="body2"
          sx={{ ml: 2.5, "&.MuiTypography-root": { fontSize: font.size.xs } }}
        >
          {getLocale() === "th"
            ? `${selectedTimeRangeData?.label?.th} · ${
                isSeller ? "สินค้าทั้งหมดของร้าน" : "สินค้าทั้งหมด"
              }`
            : `${selectedTimeRangeData?.label?.en} · ${
                isSeller ? "All products in store" : "All products"
              }`}
        </Typography>
      }
      style={{ marginBottom: 0 }}
      titleStyle={{ fontSize: font.size.lg, fontWeight: 600 }}
      headerStyle={{ marginBottom: 16 }}
    >
      <Box
        sx={{
          borderRadius: 4,
          overflow: "hidden",
          background: White,
          // padding: 4,
        }}
      >
        {data.length !== 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart
              data={data}
              margin={{ top: 20, right: 30, left: 30, bottom: 20 }}
            >
              <defs>
                <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={Primary} stopOpacity={0.3} />
                  <stop offset="100%" stopColor={Primary} stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid vertical={false} strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                tick={<CustomXAxisTick />}
                interval={xInterval}
              />
              <YAxis
                dataKey="screenPageViews"
                tickFormatter={(value) => `${value.toLocaleString()}`}
                domain={yAxisDomain}
              />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotone"
                dataKey="screenPageViews"
                stroke={Primary}
                fillOpacity={1}
                fill="url(#colorValue)"
              />
              <Line
                type="monotone"
                dataKey="screenPageViews"
                stroke={Primary}
                strokeWidth={2}
                dot={false}
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height={200}
          >
            <Typography variant="body2" color={DarkGrey}>
              {getLocale() === "th" ? "ไม่มีข้อมูล" : "No data available"}
            </Typography>
          </Box>
        )}
      </Box>
    </LabelSection>
  );
};

export default DailyViewsChart;
